import { useState, type ChangeEvent, type ElementType, type FormEvent, type ReactNode } from "react";
import { Send } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { submitHrDetails } from "@/services/marketingService";
import { corporateCarpoolCopy } from "../data/corporateCarpoolContent";

type SectionEyebrowProps = {
  icon: ElementType;
  children: ReactNode;
};

export function SectionEyebrow({ icon: Icon, children }: SectionEyebrowProps) {
  return (
    <span className="corporate-eyebrow">
      <Icon className="h-3.5 w-3.5" />
      {children}
    </span>
  );
}

const emptyForm = {
  name: "",
  email: "",
  companyName: "",
  phoneNumber: "",
  message: "",
};

export function CorporateConnectForm() {
  const t = corporateCarpoolCopy.form;
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    companyName?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const nextErrors: typeof errors = {};
    if (!formData.name.trim()) nextErrors.name = t.requiredMessage;
    if (!formData.email.trim()) {
      nextErrors.email = t.requiredMessage;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = t.invalidEmailMessage;
    }
    if (!formData.companyName.trim()) nextErrors.companyName = t.requiredMessage;
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await submitHrDetails(formData);
      toast({ title: t.successMessage });
      setFormData(emptyForm);
      setErrors({});
    } catch {
      toast({ variant: "destructive", title: t.errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="cp-connect"
      className="corporate-form-card scroll-mt-24 rounded-2xl p-6 sm:p-8"
    >
      <div className="mb-6">
        <SectionEyebrow icon={Send}>{corporateCarpoolCopy.talkEyebrow}</SectionEyebrow>
        <h2 className="mt-3 text-xl font-bold text-gray-900 sm:text-2xl">{t.title}</h2>
        <p className="mt-2 text-sm leading-relaxed text-gray-600 sm:text-base">
          {t.description}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-semibold text-gray-700">
              {t.nameLabel}
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t.namePlaceholder}
              required
              disabled={isSubmitting}
              aria-invalid={Boolean(errors.name)}
              className={`corporate-input ${errors.name ? "border-red-500 focus:border-red-500" : ""}`}
            />
            {errors.name ? (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            ) : null}
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700">
              {t.emailLabel}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t.emailPlaceholder}
              required
              disabled={isSubmitting}
              aria-invalid={Boolean(errors.email)}
              className={`corporate-input ${errors.email ? "border-red-500 focus:border-red-500" : ""}`}
            />
            {errors.email ? (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            ) : null}
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700">
              {t.companyLabel}
            </label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              placeholder={t.companyPlaceholder}
              required
              disabled={isSubmitting}
              aria-invalid={Boolean(errors.companyName)}
              className={`corporate-input ${errors.companyName ? "border-red-500 focus:border-red-500" : ""}`}
            />
            {errors.companyName ? (
              <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
            ) : null}
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700">
              {t.phoneLabel}
            </label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder={t.phonePlaceholder}
              disabled={isSubmitting}
              className="corporate-input"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700">
            {t.messageLabel}
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            placeholder={t.messagePlaceholder}
            disabled={isSubmitting}
            className="corporate-input resize-y"
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="corporate-submit inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Send className="h-4 w-4" />
          {isSubmitting ? t.submitting : t.submitButton}
        </button>
      </form>
    </section>
  );
}
